import React, { useState, useEffect } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { Menu, X, User, Search, LogOut, ShoppingCart, Heart, FileText, CreditCard, Package, Bell } from 'lucide-react';
import { useWishlist, useQuoteCart } from '../../context';
import { notificationAPI } from '../../services/api';

const Navbar = () => {
  const [isOpen, setIsOpen] = useState(false);
  const [isScrolled, setIsScrolled] = useState(false);
  const [user, setUser] = useState(null);
  const [unreadCount, setUnreadCount] = useState(0);
  const location = useLocation();
  const navigate = useNavigate();

  const { getWishlistCount } = useWishlist();
  const { getQuoteCartCount } = useQuoteCart();

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 20);
    };

    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  useEffect(() => {
    setIsOpen(false);
  }, [location]);

  // Check if user is logged in
  useEffect(() => {
    const token = localStorage.getItem('token');
    const userData = localStorage.getItem('user');
    if (token && userData) {
      setUser(JSON.parse(userData));
    }
  }, []);

  // Fetch unread notification count
  useEffect(() => {
    const fetchUnreadCount = async () => {
      if (user) {
        try {
          const response = await notificationAPI.getUnreadCount();
          if (response.data.success) {
            setUnreadCount(response.data.data.unread_count);
          }
        } catch (error) {
          console.error('Failed to fetch unread count:', error);
        }
      }
    };

    fetchUnreadCount();
    // Refresh count every 30 seconds
    const interval = setInterval(fetchUnreadCount, 30000);
    return () => clearInterval(interval);
  }, [user]);

  const handleLogout = () => {
    localStorage.removeItem('token');
    localStorage.removeItem('user');
    setUser(null);
    navigate('/login');
  };

  const publicNavLinks = [
    { name: 'Home', path: '/' },
    { name: 'Products', path: '/products' },
    { name: 'About', path: '/about' },
    { name: 'Contact', path: '/contact' },
  ];

  const userNavLinks = [
    { name: 'Home', path: '/' },
    { name: 'Products', path: '/products' },
    { name: 'Orders', path: '/dashboard?tab=orders', icon: Package },
    { name: 'Quotes', path: '/dashboard?tab=quotes', icon: FileText },
    { name: 'Finance', path: '/dashboard?tab=finance', icon: CreditCard },
  ];

  const navLinks = user ? userNavLinks : publicNavLinks;

  const isActive = (path) => {
    if (path.includes('?tab=')) {
      const tab = path.split('?tab=')[1];
      return location.pathname === '/dashboard' && location.search === `?tab=${tab}`;
    }
    return location.pathname === path;
  };

  return (
    <nav
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
        isScrolled
          ? 'bg-white shadow-lg py-3'
          : 'bg-white/95 backdrop-blur-sm py-4'
      }`}
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex justify-between items-center">
          {/* Logo */}
          <Link to="/" className="flex items-center group">
            <img
              src="/lmlogo.png"
              alt="Loha Mart"
              className="h-11 w-auto object-contain transition-transform duration-300 group-hover:scale-105"
            />
          </Link>

          {/* Desktop Navigation */}
          <div className="hidden md:flex items-center space-x-8">
            {navLinks.map((link) => (
              <Link
                key={link.path}
                to={link.path}
                className={`relative text-base font-medium transition-colors duration-300 ${
                  isActive(link.path)
                    ? 'text-primary-600'
                    : 'text-gray-700 hover:text-primary-600'
                }`}
              >
                {link.name}
                {isActive(link.path) && (
                  <span className="absolute -bottom-1 left-0 right-0 h-0.5 bg-primary-600 animate-scale-up" />
                )}
              </Link>
            ))}
          </div>

          {/* Right Section - Desktop */}
          <div className="hidden md:flex items-center space-x-4">
            <button
              className="p-2 text-gray-700 hover:text-primary-600 transition-colors duration-300 hover:bg-gray-100 rounded-lg"
              aria-label="Search"
            >
              <Search className="w-5 h-5" />
            </button>

            {/* Wishlist */}
            <Link
              to="/wishlist"
              className="relative p-2 text-gray-700 hover:text-primary-600 transition-colors duration-300 hover:bg-gray-100 rounded-lg"
              aria-label="Wishlist"
            >
              <Heart className="w-5 h-5" />
              {getWishlistCount() > 0 && (
                <span className="absolute -top-1 -right-1 w-5 h-5 bg-red-500 text-white text-xs font-bold rounded-full flex items-center justify-center">
                  {getWishlistCount()}
                </span>
              )}
            </Link>

            {/* Quote Cart */}
            <Link
              to="/quote-cart"
              className="relative p-2 text-gray-700 hover:text-primary-600 transition-colors duration-300 hover:bg-gray-100 rounded-lg"
              aria-label="Quote Cart"
              title="Quote Cart"
            >
              <ShoppingCart className="w-5 h-5" />
              {getQuoteCartCount() > 0 && (
                <span className="absolute -top-1 -right-1 w-5 h-5 bg-secondary-600 text-white text-xs font-bold rounded-full flex items-center justify-center">
                  {getQuoteCartCount()}
                </span>
              )}
            </Link>

            {user ? (
              // Logged in - Show user menu
              <>
                {/* Notifications */}
                <Link
                  to="/notifications"
                  className="relative p-2 text-gray-700 hover:text-primary-600 transition-colors duration-300 hover:bg-gray-100 rounded-lg"
                  aria-label="Notifications"
                >
                  <Bell className="w-5 h-5" />
                  {unreadCount > 0 && (
                    <span className="absolute -top-1 -right-1 w-5 h-5 bg-red-500 text-white text-xs font-bold rounded-full flex items-center justify-center">
                      {unreadCount > 9 ? '9+' : unreadCount}
                    </span>
                  )}
                </Link>
                <Link
                  to="/dashboard"
                  className="px-4 py-2 text-gray-700 hover:text-primary-600 transition-colors duration-300 hover:bg-gray-100 rounded-lg font-medium"
                >
                  Dashboard
                </Link>
                <div className="flex items-center space-x-3 pl-3 border-l border-gray-200">
                  <div className="text-right">
                    <p className="text-sm font-medium text-gray-900">
                      {user?.customer?.company_name || user?.mobile || 'User'}
                    </p>
                    <p className="text-xs text-gray-500">
                      {user?.customer?.company_name ? user?.mobile : 'Customer'}
                    </p>
                  </div>
                  <Link
                    to="/dashboard?tab=profile"
                    className="w-10 h-10 bg-gradient-to-br from-primary-500 to-secondary-500 rounded-full flex items-center justify-center hover:opacity-80 transition-opacity"
                    title="My Profile"
                  >
                    <User className="w-5 h-5 text-white" />
                  </Link>
                  <button
                    onClick={handleLogout}
                    className="p-2 text-gray-700 hover:text-red-600 transition-colors duration-300 hover:bg-gray-100 rounded-lg"
                    title="Logout"
                  >
                    <LogOut className="w-5 h-5" />
                  </button>
                </div>
              </>
            ) : (
              // Logged out - Show login button
              <>
                <Link
                  to="/login"
                  className="flex items-center space-x-2 px-4 py-2 text-gray-700 hover:text-primary-600 transition-colors duration-300 hover:bg-gray-100 rounded-lg"
                >
                  <User className="w-5 h-5" />
                  <span className="font-medium">Login</span>
                </Link>
                <Link
                  to="/get-quote"
                  className="px-6 py-2.5 bg-gradient-to-r from-primary-600 to-secondary-600 text-white rounded-lg font-medium hover:shadow-lg transform hover:-translate-y-0.5 transition-all duration-300"
                >
                  Get Quote
                </Link>
              </>
            )}
          </div>

          {/* Mobile Menu Button */}
          <button
            onClick={() => setIsOpen(!isOpen)}
            className="md:hidden p-2 text-gray-700 hover:bg-gray-100 rounded-lg transition-colors duration-300"
            aria-label="Toggle menu"
          >
            {isOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
          </button>
        </div>

        {/* Mobile Navigation */}
        {isOpen && (
          <div className="md:hidden mt-4 pb-4 animate-slide-down">
            <div className="flex flex-col space-y-2">
              {navLinks.map((link) => (
                <Link
                  key={link.path}
                  to={link.path}
                  className={`px-4 py-3 rounded-lg text-base font-medium transition-all duration-300 ${
                    isActive(link.path)
                      ? 'bg-primary-50 text-primary-600'
                      : 'text-gray-700 hover:bg-gray-100'
                  }`}
                >
                  {link.name}
                </Link>
              ))}

              {/* Quote Cart & Wishlist - Mobile */}
              <div className="flex gap-2 px-2">
                <Link
                  to="/wishlist"
                  className="flex-1 px-4 py-3 rounded-lg text-base font-medium text-gray-700 hover:bg-gray-100 flex items-center justify-center space-x-2"
                >
                  <Heart className="w-5 h-5" />
                  <span>Wishlist</span>
                  {getWishlistCount() > 0 && (
                    <span className="w-5 h-5 bg-red-500 text-white text-xs font-bold rounded-full flex items-center justify-center">
                      {getWishlistCount()}
                    </span>
                  )}
                </Link>
                <Link
                  to="/quote-cart"
                  className="flex-1 px-4 py-3 rounded-lg text-base font-medium text-gray-700 hover:bg-gray-100 flex items-center justify-center space-x-2"
                >
                  <ShoppingCart className="w-5 h-5" />
                  <span>Quote Cart</span>
                  {getQuoteCartCount() > 0 && (
                    <span className="w-5 h-5 bg-secondary-600 text-white text-xs font-bold rounded-full flex items-center justify-center">
                      {getQuoteCartCount()}
                    </span>
                  )}
                </Link>
              </div>

              <div className="border-t border-gray-200 my-2" />

              {user ? (
                // Logged in - Mobile
                <>
                  <Link
                    to="/notifications"
                    className="px-4 py-3 rounded-lg text-base font-medium text-gray-700 hover:bg-gray-100 flex items-center justify-between"
                  >
                    <div className="flex items-center space-x-2">
                      <Bell className="w-5 h-5" />
                      <span>Notifications</span>
                    </div>
                    {unreadCount > 0 && (
                      <span className="w-6 h-6 bg-red-500 text-white text-xs font-bold rounded-full flex items-center justify-center">
                        {unreadCount > 9 ? '9+' : unreadCount}
                      </span>
                    )}
                  </Link>
                  <Link
                    to="/dashboard"
                    className="px-4 py-3 rounded-lg text-base font-medium text-gray-700 hover:bg-gray-100"
                  >
                    Dashboard
                  </Link>
                  <div className="px-4 py-3 bg-gray-50 rounded-lg">
                    <p className="text-sm font-medium text-gray-900">
                      {user?.customer?.company_name || user?.mobile || 'User'}
                    </p>
                    <p className="text-xs text-gray-500">{user?.mobile}</p>
                  </div>
                  <button
                    onClick={handleLogout}
                    className="px-4 py-3 rounded-lg text-base font-medium text-red-600 hover:bg-red-50 flex items-center space-x-2"
                  >
                    <LogOut className="w-5 h-5" />
                    <span>Logout</span>
                  </button>
                </>
              ) : (
                // Logged out - Mobile
                <>
                  <Link
                    to="/login"
                    className="px-4 py-3 rounded-lg text-base font-medium text-gray-700 hover:bg-gray-100 flex items-center space-x-2"
                  >
                    <User className="w-5 h-5" />
                    <span>Login</span>
                  </Link>
                  <Link
                    to="/get-quote"
                    className="px-4 py-3 rounded-lg text-base font-medium bg-gradient-to-r from-primary-600 to-secondary-600 text-white text-center hover:shadow-lg transition-all duration-300"
                  >
                    Get Quote
                  </Link>
                </>
              )}
            </div>
          </div>
        )}
      </div>
    </nav>
  );
};

export default Navbar;
